/**
 * Date utility functions for consistent date formatting and manipulation
 */

import { stringDatePostgresToDate } from './index';

/**
 * Formats a Date object to YYYY-MM-DD string format (ISO date format)
 * Used for API communication and date keys
 * 
 * @param date - Date object to format
 * @returns Formatted date string in YYYY-MM-DD format
 * 
 * @example
 * formatDateToISO(new Date(2024, 0, 15)) // Returns "2024-01-15"
 */
export function formatDateToISO(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Gets the start of the week for a given date
 * 
 * @param date - Date to get week start for
 * @param weekStartsOn - Day of week that starts the week ('monday' | 'sunday')
 * @returns Date object representing the start of the week (00:00:00)
 * 
 * @example
 * getWeekStart(new Date(2024, 0, 17)) // Returns Monday, Jan 15, 2024 00:00:00
 */
export function getWeekStart(date: Date, weekStartsOn: 'monday' | 'sunday' = 'monday'): Date {
  const dayOfWeek = weekStartsOn === 'monday' 
    ? (date.getDay() + 6) % 7  // Monday = 0, Sunday = 6
    : date.getDay();            // Sunday = 0, Saturday = 6
  
  const weekStart = new Date(date);
  weekStart.setDate(date.getDate() - dayOfWeek);
  weekStart.setHours(0, 0, 0, 0);
  return weekStart;
}

/**
 * Gets the end of the week for a given week start date
 * 
 * @param weekStart - Date representing the start of the week
 * @returns Date object representing the end of the week (23:59:59)
 * 
 * @example
 * const weekStart = getWeekStart(new Date(2024, 0, 17));
 * getWeekEnd(weekStart) // Returns Sunday, Jan 21, 2024 23:59:59
 */
export function getWeekEnd(weekStart: Date): Date {
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  weekEnd.setHours(23, 59, 59, 999);
  return weekEnd;
}

/**
 * Gets all week start dates that contain days from a given month
 * 
 * @param year - Year of the month
 * @param month - Month index (0-11, where 0 = January)
 * @param weekStartsOn - Day of week that starts the week
 * @returns Array of Date objects representing week start dates
 * 
 * @example
 * getWeeksInMonth(2024, 0) // Returns all Monday dates in January 2024
 */
export function getWeeksInMonth(
  year: number, 
  month: number, 
  weekStartsOn: 'monday' | 'sunday' = 'monday'
): Date[] {
  const weeks: Date[] = [];
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  
  // Find the week start before or on the first day of the month
  let currentWeekStart = getWeekStart(firstDay, weekStartsOn);
  
  // Collect all weeks that contain days from this month
  while (currentWeekStart <= lastDay) {
    const weekEnd = getWeekEnd(currentWeekStart);
    
    // Include week if it overlaps with the target month
    if (weekEnd >= firstDay && currentWeekStart <= lastDay) {
      weeks.push(new Date(currentWeekStart));
    }
    
    // Move to next week
    currentWeekStart = new Date(currentWeekStart);
    currentWeekStart.setDate(currentWeekStart.getDate() + 7);
  }
  
  return weeks;
}

/**
 * Navigates to the previous or next week from a given week start
 * 
 * @param weekStart - Current week start date
 * @param direction - Direction to navigate ('prev' | 'next')
 * @returns New week start date
 * 
 * @example
 * const nextWeek = navigateWeek(currentWeekStart, 'next');
 * const prevWeek = navigateWeek(currentWeekStart, 'prev');
 */
export function navigateWeek(weekStart: Date, direction: 'prev' | 'next'): Date {
  const newWeekStart = new Date(weekStart);
  const daysToAdd = direction === 'next' ? 7 : -7;
  newWeekStart.setDate(weekStart.getDate() + daysToAdd);
  return newWeekStart;
}

/**
 * Gets Monday-based day of week (Monday = 0, Sunday = 6)
 * 
 * @param date - Date to get day of week for
 * @returns Day of week number (0-6, where 0 = Monday)
 */
export function getMondayBasedDayOfWeek(date: Date): number {
  const day = date.getDay();
  return (day + 6) % 7;
}

/**
 * Safely parses a UTC timestamp string, ensuring timezone-aware parsing
 * Handles both ISO format (with/without timezone) and PostgreSQL format
 * 
 * This function is a wrapper around stringDatePostgresToDate for consistency
 * and to provide a more descriptive name in TypeScript contexts.
 * 
 * @param dateString - Date string from server (may be ISO or PostgreSQL format) or Date object
 * @returns Date object parsed as UTC
 * 
 * @example
 * parseUTCString("2024-01-15T10:00:00Z") // Returns Date in UTC
 * parseUTCString("2024-01-15 10:00:00") // Assumes UTC, returns Date in UTC
 * parseUTCString("2024-01-15T10:00:00+05:00") // Parses with timezone offset
 */
export function parseUTCString(dateString: string | Date): Date {
  return stringDatePostgresToDate(dateString);
}

